import { componentService } from '../services/componentService';
import { acronymService } from '../services/acronymService';
import { pointService } from '../services/pointService';
import { PhChunkObject } from '../types';

export interface ProcessedFeatureInfo {
  selectedFeature: any;
  errataNote: any;
  ecoSiteInfo: any;
  irrigDegreeoflimitaion: any;
  phProportions: any[];
  progressBarTypes: string[];
  soilpitInfo: any;
  horizonsInfo: any[];
  pointType: string;
}

export async function displayFeatureInfo(feature: any): Promise<ProcessedFeatureInfo> {
  const result: ProcessedFeatureInfo = {
    selectedFeature: null,
    errataNote: null,
    ecoSiteInfo: null,
    irrigDegreeoflimitaion: null,
    phProportions: [],
    progressBarTypes: [],
    soilpitInfo: null,
    horizonsInfo: [],
    pointType: '',
  };

  if (!feature) {
    return result;
  }

  if (feature.geometry?.type === 'Point') {
    result.pointType = feature.properties?.datatype || '';

    if (feature.properties?.datatype === 'soilpit' && feature.id) {
      try {
        const soilPitResponse = await pointService.getSoilPitId(feature.id.split('.')[1]);
        if (soilPitResponse.soil_pits_id) {
          const soilPitInfo = await pointService.getSoilPitInfo(soilPitResponse.soil_pits_id);
          const horizonsInfo = await pointService.getSoilPitHorizonInfo(
            soilPitResponse.soil_pits_id,
          );

          result.soilpitInfo = soilPitInfo;
          result.horizonsInfo = horizonsInfo.resource || [];
        }
      } catch (error) {
        console.error('Error fetching soil pit data:', error);
      }
    }
  } else if (feature.properties?.poly_id) {
    const polygonId = feature.properties.poly_id;

    try {
      const [errataResponse, ecoSiteResponse, irrigResponse] = await Promise.allSettled([
        componentService.getErrataForPolygon(polygonId),
        componentService.getEcoSiteForPolygon(polygonId),
        componentService.getInitialIrrigationData(feature.properties.irrig),
      ]);

      if (errataResponse.status === 'fulfilled' && errataResponse.value.resource?.length > 0) {
        result.errataNote = errataResponse.value.resource[0];
      }

      if (ecoSiteResponse.status === 'fulfilled' && ecoSiteResponse.value.resource?.length > 0) {
        result.ecoSiteInfo = ecoSiteResponse.value.resource[0];
      }

      if (irrigResponse.status === 'fulfilled' && irrigResponse.value.resource?.length > 0) {
        result.irrigDegreeoflimitaion = irrigResponse.value.resource[0].irrig_degreeoflimitation;
      }

      if (feature.properties.capblty) {
        try {
          const prettyAgCap = acronymService.agCapabilityToBreakdown(feature.properties.capblty);
          feature.properties.prettyAgCap = prettyAgCap;

          const agCapDesc = await acronymService.agCapabilityToDescription(
            feature.properties.capblty,
          );
          feature.properties.agCapDesc = agCapDesc[0] || '';
        } catch (error) {
          console.error('Error processing ag capability:', error);
        }
      }

      if (feature.properties.salsym) {
        try {
          const salDesc = await acronymService.salityClassToDescription(feature.properties.salsym);
          feature.properties.salDesc = salDesc[0] || '';
        } catch (error) {
          console.error('Error processing salinity:', error);
        }
      }

      if (feature.properties.phsym) {
        try {
          const phDefinition = await acronymService.phToDefinition(feature.properties.phsym);
          const dt: any[] = [];
          phDefinition.forEach((element) => {
            if (!dt.includes(element)) {
              dt.push(element);
            }
          });
          feature.properties.phDefinition = dt;
          result.phProportions = ParsepH(dt);
        } catch (error) {
          console.error('Error processing pH definition:', error);
        }
      }

      if (feature.properties.polygon_label) {
        try {
          let uppercaseCode = feature.properties.polygon_label.split(/[0-9]/)[0].toUpperCase();
          if (feature.properties.mapumod) {
            uppercaseCode += feature.properties.mapumod.toUpperCase();
          }
          const assocDesc =
            await acronymService.polygonLabelToAssociationDescription(uppercaseCode);
          feature.properties.assocDescription = assocDesc[0] || '';
        } catch (error) {
          console.error('Error processing association description:', error);
        }
      }

      if (feature.properties.polygon_label && feature.properties.polygon_label.includes(':')) {
        try {
          const mapunitDesc = await acronymService.mapunitToDescription(
            feature.properties.polygon_label,
          );
          feature.properties.mapunitDescription = mapunitDesc[0] || '';
        } catch (error) {
          console.error('Error processing mapunit description:', error);
        }
      }
    } catch (error) {
      console.error('Error processing polygon feature:', error);
    }
  }

  result.selectedFeature = feature;
  return result;
}

export function cleanPolygonAttributes(feature: any): any {
  if (!feature?.properties) return feature;

  const cleanedFeature = { ...feature };
  const props = cleanedFeature.properties;

  Object.keys(props).forEach((key) => {
    if (props[key] === null || props[key] === undefined || props[key] === '') {
      delete props[key];
    }
  });

  return cleanedFeature;
}

export function buildChart(components: any[]): any[] {
  if (!components || components.length === 0) return [];

  return components.map((component, index) => ({
    name: component.soilname || `Component ${index + 1}`,
    value: component.percent || 0,
    fill: getChartColor(index),
  }));
}

function getChartColor(index: number): string {
  const colors = [
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff7300',
    '#00ff00',
    '#ff00ff',
    '#00ffff',
    '#ffff00',
  ];
  return colors[index % colors.length];
}

function ParsepH(data: any[]): PhChunkObject[] {
  class PhChunkObject {
    percent: number;
    description: string;

    constructor(percent?: number, description?: string) {
      this.percent = percent || 0;
      this.description = description || '';
    }
  }

  const allMyObjects: PhChunkObject[] = [];

  const eachChunk: string[] = data.toString().split(', ');

  eachChunk.forEach((chunk) => {
    const parsedChunk: string[] = chunk.split('% ');
    allMyObjects.push(new PhChunkObject(parseFloat(parsedChunk[0]), parsedChunk[1]));
  });

  return allMyObjects;
}
